import type { RuleGroupType } from 'react-querybuilder';

import type {
  GlobalFieldInfoListGlobalFieldInfo,
  GlobalPolicyCategoryInfoListGlobalPolicyCategoryInfo,
} from '~/server_shared/openapi/data-contracts';
import {
  GlobalFieldInfoListGlobalFieldAnchor,
  GlobalFieldInfoListGlobalFieldPlacement,
} from '~/server_shared/openapi/data-contracts';

/**
 * The generated contracts with the one field each screen needs narrowed: `conditions_json` and
 * `definition` arrive as untyped objects but are always the RQB tree and the configured-field
 * definition their consumers parse. Derived from the generated types rather than re-typed, so a
 * row cannot drift from the BE schema.
 */
export type GlobalFieldRow = GlobalFieldInfoListGlobalFieldInfo;

export type GlobalCategoryRow = Omit<GlobalPolicyCategoryInfoListGlobalPolicyCategoryInfo, 'conditions_json'> & {
  conditions_json: RuleGroupType | null;
};

export const globalFieldsUrl = (organizationId: number): string =>
  `/sysconfig/api/v1/organizations/${organizationId}/global_configured_fields`;

export const globalCategoriesUrl = (organizationId: number): string => `${globalFieldsUrl(organizationId)}/categories`;

/**
 * Where a global field is projected, and where in its section it lands.
 *
 * Aliased from the generated contract rather than re-declared: the generator emits one enum per
 * schema (`CreateGlobalFieldParams…`, `GlobalFieldInfo…` and two more siblings) which are all the
 * same backend enum, so the screen picks the one on the row type it actually renders. A
 * hand-written copy would typecheck against nothing and drift the moment the backend gains a
 * placement.
 */
export const GLOBAL_FIELD_PLACEMENT = GlobalFieldInfoListGlobalFieldPlacement;
export type GlobalFieldPlacement = GlobalFieldInfoListGlobalFieldPlacement;

export const GLOBAL_FIELD_ANCHOR = GlobalFieldInfoListGlobalFieldAnchor;
export type GlobalFieldAnchor = GlobalFieldInfoListGlobalFieldAnchor;

/** The FNOL tab's placements, in the order the form itself renders them. */
export const FNOL_PLACEMENTS: GlobalFieldPlacement[] = [
  GLOBAL_FIELD_PLACEMENT.IncidentDetails,
  GLOBAL_FIELD_PLACEMENT.SupplementaryInformation,
  GLOBAL_FIELD_PLACEMENT.FirstPartyInvolved,
  GLOBAL_FIELD_PLACEMENT.ThirdPartyInvolved,
];

export const PLACEMENT_LABELS: Record<GlobalFieldPlacement, string> = {
  [GLOBAL_FIELD_PLACEMENT.Policy]: 'Policy',
  [GLOBAL_FIELD_PLACEMENT.IncidentDetails]: 'Incident Details',
  [GLOBAL_FIELD_PLACEMENT.SupplementaryInformation]: 'Supplementary Information',
  [GLOBAL_FIELD_PLACEMENT.FirstPartyInvolved]: 'First Party Involved',
  [GLOBAL_FIELD_PLACEMENT.ThirdPartyInvolved]: 'Third Party Involved',
};

export const ANCHOR_LABELS: Record<GlobalFieldAnchor, string> = {
  [GLOBAL_FIELD_ANCHOR.Start]: 'Before configuration fields',
  [GLOBAL_FIELD_ANCHOR.End]: 'After configuration fields',
};
